import {match} from 'ts-pattern';

import {countBy, isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  FINANCING_ENDING_BULK_OPERATION_STATE,
  type FinancingEndingBulkOperationItemResponseBody,
  type FinancingEndingBulkOperationResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {buildArray} from 'shared/web';

import {FINANCING_ENDING_OPERATION_ITEM_RESULT} from '../constants/FINANCING_ENDING_OPERATION_ITEM_RESULT';
import {FINANCING_ENDING_RUNNING_OPERATION_STATES} from '../constants/FINANCING_ENDING_RUNNING_OPERATION_STATES';
import {FINANCING_ENDING_STATUS_SEPARATOR} from '../constants/FINANCING_ENDING_STATUS_SEPARATOR';
import {getFinancingEndingOperationItemResult} from './getFinancingEndingOperationItemResult';

type EmailOutcomeCounts = {
  sentCount: number;
  notDeliveredCount: number;
  pendingCount: number;
};

const countLoadedEmailOutcomes = (
  items: FinancingEndingBulkOperationItemResponseBody[]
): EmailOutcomeCounts => {
  const resultCounts = countBy(
    (result) => result,
    items.flatMap((item) => getFinancingEndingOperationItemResult(item) ?? [])
  );
  return {
    sentCount: resultCounts[FINANCING_ENDING_OPERATION_ITEM_RESULT.SENT] ?? 0,
    notDeliveredCount:
      (resultCounts[FINANCING_ENDING_OPERATION_ITEM_RESULT.NOT_DELIVERED] ?? 0) +
      (resultCounts[FINANCING_ENDING_OPERATION_ITEM_RESULT.BLOCKED] ?? 0),
    pendingCount: items.filter(
      (item) =>
        isNotNil(item.emailOutcome) &&
        getFinancingEndingOperationItemResult(item) ===
          FINANCING_ENDING_OPERATION_ITEM_RESULT.PENDING
    ).length,
  };
};

const getEmailOutcomeCounts = (
  operation: FinancingEndingBulkOperationResponseBody,
  items: FinancingEndingBulkOperationItemResponseBody[]
): EmailOutcomeCounts =>
  isNil(operation.emailOutcomeCounts)
    ? countLoadedEmailOutcomes(items)
    : {
        sentCount: operation.emailOutcomeCounts.sent,
        notDeliveredCount:
          operation.emailOutcomeCounts.failed + operation.emailOutcomeCounts.blocked,
        pendingCount: operation.emailOutcomeCounts.pending,
      };

const getDeliveryVerdict = (
  operation: FinancingEndingBulkOperationResponseBody,
  counts: EmailOutcomeCounts,
  isFullyDone: boolean
) =>
  match(counts)
    .when(
      () => counts.notDeliveredCount > 0 && counts.sentCount === 0 && counts.pendingCount === 0,
      () => ({
        variant: 'error' as const,
        message: i18n.t('entity.businessCase.financingEnding.operation.verdict.noneDelivered', {
          count: counts.notDeliveredCount,
        }),
      })
    )
    .when(
      () => counts.notDeliveredCount > 0,
      () => ({
        variant: 'warning' as const,
        message: i18n.t('entity.businessCase.financingEnding.operation.verdict.notDelivered', {
          count: counts.notDeliveredCount,
          total: counts.sentCount + counts.notDeliveredCount + counts.pendingCount,
        }),
      })
    )
    .when(
      () => counts.pendingCount > 0,
      () => ({
        variant: operation.failedCount > 0 ? ('warning' as const) : ('neutral' as const),
        message: i18n.t('entity.businessCase.financingEnding.operation.verdict.pendingDelivery', {
          count: counts.pendingCount,
        }),
      })
    )
    .otherwise(() => ({
      variant: isFullyDone ? ('success' as const) : ('warning' as const),
      message: i18n.t('entity.businessCase.financingEnding.operation.verdict.allSent', {
        count: counts.sentCount,
      }),
    }));

const getFinishedVerdict = (
  operation: FinancingEndingBulkOperationResponseBody,
  counts: EmailOutcomeCounts,
  isRunning: boolean,
  processedCount: number
) => {
  const hasEmailOutcomes = counts.sentCount + counts.notDeliveredCount + counts.pendingCount > 0;
  const isFullyDone =
    operation.state === FINANCING_ENDING_BULK_OPERATION_STATE.DONE &&
    operation.failedCount === 0 &&
    operation.skippedCount === 0;

  return match({state: operation.state, isRunning, hasEmailOutcomes})
    .with({state: FINANCING_ENDING_BULK_OPERATION_STATE.RESOLVE_FAILED}, () => ({
      variant: 'error' as const,
      message: i18n.t('entity.businessCase.financingEnding.operation.resolveFailed'),
    }))
    .with({state: FINANCING_ENDING_BULK_OPERATION_STATE.CANCELLED}, () => ({
      variant: 'warning' as const,
      message: i18n.t('entity.businessCase.financingEnding.result.cancelled'),
    }))
    .with({isRunning: true}, () => ({
      variant: 'info' as const,
      message: i18n.t('entity.businessCase.financingEnding.operation.verdict.running', {
        done: processedCount,
        total: operation.totalCount,
      }),
    }))
    .with({hasEmailOutcomes: false}, () => ({
      variant: match({isFullyDone, state: operation.state})
        .with({isFullyDone: true}, () => 'success' as const)
        .with({state: FINANCING_ENDING_BULK_OPERATION_STATE.FAILED}, () => 'error' as const)
        .otherwise(() => 'warning' as const),
      message: i18n.t('entity.businessCase.financingEnding.result.finishedNoCounts'),
    }))
    .otherwise(() => getDeliveryVerdict(operation, counts, isFullyDone));
};

const getSummary = (
  operation: FinancingEndingBulkOperationResponseBody,
  counts: EmailOutcomeCounts
) =>
  buildArray([
    i18n.t('entity.businessCase.financingEnding.operation.summary.rowCount', {
      count: operation.totalCount,
    }),
    i18n.t('entity.businessCase.financingEnding.operation.summary.sent', {
      count: counts.sentCount,
    }),
    i18n.t('entity.businessCase.financingEnding.operation.summary.notDelivered', {
      count: counts.notDeliveredCount,
    }),
  ])
    .when(
      operation.skippedCount > 0,
      i18n.t('entity.businessCase.financingEnding.summary.skippedCount', {
        count: operation.skippedCount,
      })
    )
    .when(
      counts.pendingCount > 0,
      i18n.t('entity.businessCase.financingEnding.operation.summary.pending', {
        count: counts.pendingCount,
      })
    )
    .when(
      operation.failedCount > 0,
      i18n.t('entity.businessCase.financingEnding.operation.summary.rowsFailed', {
        count: operation.failedCount,
      })
    )
    .join(FINANCING_ENDING_STATUS_SEPARATOR);

const getContextParts = (operation: FinancingEndingBulkOperationResponseBody) =>
  [
    {key: 'template', text: operation.templateName},
    {key: 'mailbox', text: operation.mailbox?.label},
    {
      key: 'sentBy',
      text: isNotNilOrEmpty(operation.sentBy)
        ? i18n.t('entity.businessCase.financingEnding.operation.sentBy', {
            sentBy: operation.sentBy,
          })
        : null,
    },
  ].filter((part) => isNotNilOrEmpty(part.text));

/**
 * A cancelled operation says so as a warning, whatever its e-mails did; the summary still counts
 * them. The e-mail counts come from the operation and cover all its items. A backend without them leaves
 * the loaded items to count, and then the verdict is never a success while some rows are not
 * loaded. A success needs a DONE operation without skipped or failed rows and without
 * pending or not delivered e-mails. Without any `emailOutcome` (all rows skipped, or an older
 * backend) the verdict says only that the processing finished. The summary always shows the sent and
 * not delivered e-mails; its other counts show only when they are not zero. While e-mails wait for
 * delivery and neither an e-mail nor a row failed, the variant is `neutral`, not a coloured result.
 * The context parts name the template, the mailbox and the sender that are known.
 */
export const getFinancingEndingOperationVerdict = (
  operation: FinancingEndingBulkOperationResponseBody,
  items: FinancingEndingBulkOperationItemResponseBody[]
) => {
  const counts = getEmailOutcomeCounts(operation, items);
  const processedCount = operation.doneCount + operation.failedCount + operation.skippedCount;
  const isRunning = FINANCING_ENDING_RUNNING_OPERATION_STATES.includes(operation.state);
  const finishedVerdict = getFinishedVerdict(operation, counts, isRunning, processedCount);

  const isPartiallyLoaded = !isRunning && items.length < operation.totalCount;
  const variant =
    isPartiallyLoaded &&
    isNil(operation.emailOutcomeCounts) &&
    finishedVerdict.variant === 'success'
      ? ('info' as const)
      : finishedVerdict.variant;

  const message = buildArray([finishedVerdict.message])
    .when(
      operation.skippedCount > 0 && !isRunning,
      i18n.t('entity.businessCase.financingEnding.summary.skippedCount', {
        count: operation.skippedCount,
      })
    )
    .when(
      isPartiallyLoaded,
      i18n.t('entity.businessCase.financingEnding.operation.loadedOf', {
        loaded: items.length,
        total: operation.totalCount,
      })
    )
    .join(FINANCING_ENDING_STATUS_SEPARATOR);

  return {
    variant,
    message,
    summary: getSummary(operation, counts),
    contextParts: getContextParts(operation),
    isRunning,
    processedCount,
  };
};
